import { z } from "zod";
import { CONFLICT_MSG } from "./concurrent.js";
import { TaxCategory } from "./tax.js";
import { LineInput, PICKER_MSG } from "./estimate.js";
import { evalTakeoff } from "./takeoff.js";

// ---------------------------------------------------------------------------
// スマホの見積の土台（3回目、#117、docs/44 §4・§6・§9、docs/47 §4・§10、DB 20261003_2345）。
// スマホは「見積の土台を作る道具」：行（名称・規格・数量・単位・場所・工種・外注先）・写真・社内メモ。単価・金額・仕入れは出さない。
// 行は行ごとに保存する（PATCH /estimates/:id/lines/:line）。版（Estimate.updatedAt）を一緒に送り、PC の丸ごとの保存とぶつかれば 409。
// 写真とメモは行の系譜に付く（回を重ねても・並べ替えても付いたまま）。行を消すと「行に入れていない」に戻り、同じ系譜で戻すと一緒に戻る
// ---------------------------------------------------------------------------

/** 画面に出す文（docs/10 §5.1.2）。{n} などは呼ぶ側で入れる */
export const PHONE_MSG = {
  title: "見積書",
  newEstimate: "新しい見積",
  search: "検索",
  searchPlaceholder: "{subject}・{customer}で探す",
  lines: "行 {n}",
  photos: "写真 {n}",
  /** #142 段6 No.43・44：言葉の {vendor}（外注先／仕入先） */
  vendorMissing: "{vendor} 未定",
  vendorMissingCount: "{vendor} 未定 {n}",
  unassignedPhotos: "行に入れていない写真 {n}枚",
  unassignedMemos: "行に入れていないメモ {n}件",
  provisional: "仮",
  pcEdit: "PCで編集",
  shoot: "撮影",
  pick: "選択",
  addLine: "行",
  addHeading: "見出し",
  add: "追加",
  memo: "社内メモ",
  memoHeading: "社内メモ（顧客には出ません）",
  memoCount: "メモ {n}",
  addMemo: "メモ追加",
  sendMemo: "メモ送信",
  vendor: "{vendor}",
  noVendor: "{vendor}なし",
  caption: "ひとこと",
  groupBy: "まとめ方",
  place: "場所",
  trade: "{trade}",
  other: "その他",
  rowsInGroup: "{n}行",
  fold: "折りたたみ",
  unfold: "展開",
  editLine: "行を編集",
  viewLine: "行を見る",
  done: "完了",
  close: "閉じる",
  name: "{lineName}",
  /** 画面は lineSpecLabel（既定の「規格」なら「規格・仕様」。#222） */
  spec: "{lineSpec}",
  quantity: "数量（式も打てる）",
  unit: "単位",
  kind: "行の種類",
  kindLabel: { detail: "ふつう", group: "見出し", note: "文章" },
  up: "上へ",
  down: "下へ",
  deleteLine: "行削除",
  deleted: "「{name}」を削除しました",
  undo: "元に戻す",
  addPlace: "＋ 場所追加",
  placeName: "場所の名前（例 2F ＞ 子供部屋）",
  noPlace: "場所なし",
  noTrade: "{trade}なし",
  openPc: "PC版で表示",
  /** #142 段3（Q4）：「見積全体の現場メモ」→「見積メモ」 */
  siteNote: "見積メモ",
  siteNoteHelp: "要望など、行にならないこと（{customer}には出ません）",
  saveNote: "メモ保存",
  noteSaved: "見積のメモを保存しました",
  /** 使う機能（#142 段3）：場所・{trade}を両方とも使わない会社のまとめ（1つの帯） */
  allRows: "すべて",
  /** 使う機能「{takeoff}」が OFF の会社：数量は数字だけ（式は打てない） */
  numberOnly: "数字で入れてください",
  discard: "下書き削除",
  discarded: "下書きを捨てました",
  more: "その他の操作",
  back: "見積書",
  stale: CONFLICT_MSG.auto,
  pending: "送り待ち {n}枚",
  pendingHelp: "電波が戻ったら順に送ります（アプリを閉じても残ります）",
  readOnly: "出した見積です。{lineName}・数量は変えられません（写真・メモは足せます）",
  /** 題の下の小さなチップ（#171。説明は上の readOnly を title に） */
  issuedChip: "発行済",
  /** 写真（field）の無い会社（#142 段6 No.47） */
  readOnlyNoPhoto: "出した見積です。{lineName}・数量は変えられません（メモは足せます）",
  newLineTitle: "追加",
  newLineName: "{lineName}",
  createLine: "決定",
  newHeadingTitle: "見出しを足す",
  createHeading: "見出し追加",
  moveTo: "ほかの行へ",
  moveToTitle: "どの行に入れますか",
  toLine: "行に追加",
  noLine: "行に入れない",
  removePhoto: "写真削除",
  photoRemoved: "写真を消しました",
  photoMoved: "写真を「{name}」に入れました",
  photoUnassigned: "写真を行から外しました",
  takeoffMany: "拾い出し {n}件（PCで編集）",
  formulaEquals: "＝ {v}",
  formulaInvalid: "式が読めません（数字と ＋ − × ÷ （ ） だけ）",
  empty: "まだ見積がありません",
  emptyLines: "まだ行がありません。「＋ 行」か「撮影」から始めます",
  /** 写真（field）の無い会社（#142 段6 No.46） */
  emptyLinesNoPhoto: "まだ行がありません。「＋ 行」から始めます",
  candidates: "候補",
  /** #215：PC の候補の引き出しのタブと同じ言葉 */
  candidatesHere: PICKER_MSG.tabs.here,
  candidatesPast: PICKER_MSG.tabs.past,
  noCandidates: "候補はありません",
  times: "{n}回",
  /** API のエラー */
  stale409: CONFLICT_MSG.api,
  photoLimit: "写真は1つの見積に500枚までです",
  photoType: "写真は JPEG だけです（画面で縮めてから送ります）",
  photoTooBig: "写真が大きすぎます（長辺 1600px・2MB まで、小さい絵は長辺 400px まで）",
  photoBroken: "写真を読めませんでした",
  /**
   * 送り待ちの列（photoQueue）で断られた写真・振り分け（#178）。どの画面にいても右下の通知（誤り＝× まで消えない）。
   * {what}＝写真／写真の振り分け、{subject}＝その見積の件名（列に入れたときの名前。無い古い列は件名なしの形）、{reason}＝API の文
   */
  queueRejected: "{what}を送れませんでした（{subject}）：{reason}",
  queueRejectedNoSubject: "{what}を送れませんでした：{reason}",
  queuePhoto: "写真",
  queueAssign: "写真の振り分け",
  photoNotFound: "その写真は見つかりません",
  lineNotFound: "その行は見つかりません",
  lineNotPhone: "この行はスマホでは修正できません（PCで編集）",
  memoEmpty: "メモを入れてください",
  memoTooLong: "メモは1000文字までです",
  noteTooLong: "見積のメモは2000文字までです",
  nameRequired: "{lineName}を入れてください",
  quantityInvalid: "数量は 0 以上の数か式で入れてください",
} as const;

/** 写真の大きさ（画面で縮めてから送る。API は確かめるだけ。docs/13・CLAUDE.md「サーバーの制約」） */
export const LINE_PHOTO = {
  displayPx: 1600,
  thumbPx: 400,
  displayBytes: 2 * 1024 * 1024,
  thumbBytes: 300 * 1024,
  perEstimate: 500,
  quality: 0.8,
} as const;


/** スマホで作れる・直せる行の種類（ふつう／見出し／文章）。小計・率・別途・値引きは「PCで編集」 */
export const PhoneLineRole = z.enum(["detail", "group", "note"]);
export type PhoneLineRole = z.infer<typeof PhoneLineRole>;
export const isPhoneRole = (r: string): r is PhoneLineRole => r === "detail" || r === "group" || r === "note";

const Version = z.string().min(1).max(40);
const Uuid = z.string().uuid();

/** POST /estimates/:id/lines/provisional：仮の行を作る（名称だけで作れる）。見出し・文章もここから */
export const PhoneLineCreate = z.object({
  version: Version,
  role: PhoneLineRole.default("detail"),
  name: z.string().trim().min(1, PHONE_MSG.nameRequired).max(200),
  placeId: Uuid.nullable().optional(),
  tradeId: Uuid.nullable().optional(),
});
export type PhoneLineCreate = z.infer<typeof PhoneLineCreate>;

/**
 * PATCH /estimates/:id/lines/:line（:line は行の id か系譜）。省いた欄は変えない。
 * quantityText：数字だけなら手入力（manual）、式なら拾い出し（#130）として保存し行は takeoff。空なら 0（手入力）。
 * swapWith：並べ替え（上へ／下へ）＝その行と入れ替える（行の id か系譜）
 */
export const PhoneLinePatch = z.object({
  version: Version,
  name: z.string().trim().max(200).optional(),
  spec: z.string().trim().max(200).optional(),
  quantityText: z.string().max(200).optional(),
  unit: z.string().trim().max(20).optional(),
  placeId: Uuid.nullable().optional(),
  tradeId: Uuid.nullable().optional(),
  vendorPartnerId: Uuid.nullable().optional(),
  /** 税区分（T2。行のシートの「税区分」。新コード7つだけ） */
  taxCategory: TaxCategory.optional(),
  role: PhoneLineRole.optional(),
  swapWith: Uuid.optional(),
});
export type PhoneLinePatch = z.infer<typeof PhoneLinePatch>;

/** DELETE /estimates/:id/lines/:line の答え・「元に戻す」（POST /estimates/:id/lines/restore）に送り返すもの */
export const PhoneLineRestore = z.object({
  version: Version,
  lineageId: Uuid,
  /** 元の並び（その位置に戻す。ほかの行は1つずつ下がる） */
  lineNo: z.number().int().min(1),
  line: LineInput,
  /** 外注見積から入れた仕入れ（047）だったとき、その外注見積。同じ額のまま戻すなら出どころも戻す */
  costVendorQuoteId: Uuid.nullable().optional(),
});
export type PhoneLineRestore = z.infer<typeof PhoneLineRestore>;

/**
 * 行の写真（GET /estimates/:id/photos）。url・thumbUrl は短命の署名つき（15〜30分）。
 * #118 から1枚が何行にも付く：lineageIds＝付いている行の系譜（行を消した系譜も）、lineIds＝そのうち いま開いている回にある行（空＝行に入れていない）
 */
export const LinePhoto = z.object({
  id: Uuid,
  lineageIds: z.array(Uuid),
  lineIds: z.array(Uuid),
  caption: z.string(),
  sortNo: z.number().int(),
  url: z.string(),
  thumbUrl: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  createdAt: z.string(),
  createdByName: z.string(),
  clientUuid: Uuid.nullable(),
});
export type LinePhoto = z.infer<typeof LinePhoto>;

/** 行の社内メモ（新しい順）。直せない・消せない */
export const LineMemo = z.object({
  id: Uuid,
  lineageId: Uuid,
  body: z.string(),
  createdAt: z.string(),
  createdByName: z.string(),
});
export type LineMemo = z.infer<typeof LineMemo>;
export const LineMemoList = z.object({ items: z.array(LineMemo) });
export type LineMemoList = z.infer<typeof LineMemoList>;
/** POST /estimates/:id/lines/:line/memos */
export const LineMemoCreate = z.object({ body: z.string().trim().min(1, PHONE_MSG.memoEmpty).max(1000, PHONE_MSG.memoTooLong) });
export type LineMemoCreate = z.infer<typeof LineMemoCreate>;

/** 見積の写真の一覧。行に入れていない写真（unassignedCount）と、行を消して行に入れていないメモ（unassignedMemos）も */
export const LinePhotoList = z.object({
  items: z.array(LinePhoto),
  unassignedCount: z.number().int(),
  total: z.number().int(),
  limit: z.number().int(),
  unassignedMemos: z.array(LineMemo),
});
export type LinePhotoList = z.infer<typeof LinePhotoList>;

/**
 * PUT /estimates/:id/photos/:pid：付け替え・ひとこと・並び。省いた欄は変えない。
 * 付け替え（lineId か lineLineageId。null＝行から外す）：fromLineId があればその行の結び付きだけを入れ替える（ほかの行の結び付きは残す。#118）、
 * 無ければ全部の結び付きを入れ替える
 */
export const LinePhotoUpdate = z.object({
  lineId: Uuid.nullable().optional(),
  lineLineageId: Uuid.nullable().optional(),
  fromLineId: Uuid.optional(),
  caption: z.string().trim().max(200).optional(),
  sortNo: z.number().int().min(0).max(100000).optional(),
});
export type LinePhotoUpdate = z.infer<typeof LinePhotoUpdate>;

/** 見積全体の現場メモ（GET/PUT /estimates/:id/note） */
export const EstimateNote = z.object({ body: z.string(), updatedAt: z.string().nullable(), updatedByName: z.string() });
export type EstimateNote = z.infer<typeof EstimateNote>;
export const EstimateNoteUpdate = z.object({ body: z.string().max(2000, PHONE_MSG.noteTooLong) });
export type EstimateNoteUpdate = z.infer<typeof EstimateNoteUpdate>;

/* ---------------- 数量の欄（数字だけ＝手入力、式＝拾い出し） ---------------- */

export type PhoneQuantity =
  | { kind: "empty" }
  | { kind: "number"; value: number }
  | { kind: "formula"; formula: string; value: number }
  | { kind: "invalid" };

const ZEN = "０１２３４５６７８９．";
const HAN = "0123456789.";
const toHalf = (s: string): string => [...s].map((c) => { const i = ZEN.indexOf(c); return i >= 0 ? HAN[i]! : c; }).join("");
const QTY_MAX = 99_999_999;

/**
 * 数量の欄の文字を読む。全角の数字・小数点は半角に。
 *   ・数字だけ（12・12.5・１２．５）→ number（小数2桁に四捨五入。0〜99,999,999）＝行は手入力（manual）
 *   ・式（12.5×9.6・(3.6+2.4)*2.4）→ formula（答えは DB と同じ evalTakeoff）＝拾い出しとして保存し行は takeoff
 *   ・空 → empty（数量 0 の手入力）、読めない → invalid
 */
export function parsePhoneQuantity(text: string): PhoneQuantity {
  const t = toHalf(text).trim();
  if (!t) return { kind: "empty" };
  if (/^\d+(\.\d*)?$|^\.\d+$/.test(t)) {
    const v = Math.round(Number(t) * 100) / 100;
    return Number.isFinite(v) && v >= 0 && v <= QTY_MAX ? { kind: "number", value: v } : { kind: "invalid" };
  }
  const v = evalTakeoff(text);
  if (v == null) return { kind: "invalid" };
  return { kind: "formula", formula: text.trim(), value: v };
}

/** 数量の見せ方（小数2桁まで・うしろの 0 は出さない）：120・9.72 */
export function phoneQty(q: number | null | undefined): string {
  if (q == null) return "";
  return String(Math.round(q * 100) / 100);
}
